import { ReactElement, useCallback } from 'react';

import {
   EzwmAccountBlockedFault,
   EzwmAccountLoginFault,
   EzwmPasswordExpiredFault,
   MedicalDeviceOrderApproveCommand,
   MedicalDeviceOrderApproveResult,
   MedicalDeviceOrderError,
} from '@dreryk/egabinet-ezwm-frontend-api';
import { VisitMedicalDeviceOrderCreateDraftCommand } from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent, useNavTabsContext } from '@dreryk/react-components';
import { createSingleInterpolatedTranslationKey } from '@dreryk/react-common';
import { Choice } from '@dreryk/watermelon';

import { visitPaths } from '~libs.desktop.shared.paths';
import { useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';
import { isNfzMfaLoginFault, useEzwmMfaGuard } from '~legacy.personnel';

import { useVisitDocumentContext } from '../../../common/visitDocument/VisitDocumentContext';
import { medicalDevicesTranslationKeys } from '../translations';

import { MedicalDeviceOrderAllFieldsFormBean } from './medicalDeviceOrder.types';
import { mapFormBeanToMedicalDeviceOrderDetails } from './medicalDeviceOrder.utils';

const DEVICE_ORDER_ERROR_STATUSES = ['INCORRECT', 'REJECTED'];

export type MedicalDeviceOrderEzwmAuthenticationFault =
   | EzwmAccountLoginFault
   | EzwmAccountBlockedFault
   | EzwmPasswordExpiredFault;

type UseMedicalDeviceOrderApproveCommandOptions = {
   onEzwmLoginError?: (error: MedicalDeviceOrderEzwmAuthenticationFault) => void;
   onDocumentVerificationErrors?: (errors: MedicalDeviceOrderError[]) => void;
   onSuccess: VoidFunction;
   onAuthenticationRequiredFault: VoidFunction;
};

type UseMedicalDeviceOrderApproveCommandParameters = {
   formData: MedicalDeviceOrderAllFieldsFormBean;
   externalId: string;
   mfaTotp?: string;
};

export const useMedicalDeviceOrderApproveCommand = (
   options: UseMedicalDeviceOrderApproveCommandOptions
): UseCqrsCommandResult<
   MedicalDeviceOrderApproveResult,
   UseMedicalDeviceOrderApproveCommandParameters,
   MedicalDeviceOrderEzwmAuthenticationFault
> => {
   const { changeTab } = useNavTabsContext<visitPaths.MedicalDevicesPanelNavParams>();
   const { onAuthenticationRequiredFault } = options;
   const certificateFaultHandlers = useCertificateFaultHandlerMap({
      onAuthenticationRequired: onAuthenticationRequiredFault,
   });

   return useCqrsCommand(
      (parameters) => {
         const command = new MedicalDeviceOrderApproveCommand();
         command.externalId = parameters.externalId;
         command.details = mapFormBeanToMedicalDeviceOrderDetails(parameters.formData);

         if (parameters.mfaTotp) {
            command.mfaTotp = Choice.of(parameters.mfaTotp);
         }

         return command;
      },
      {
         onSuccess: (result) => {
            if (options.onDocumentVerificationErrors && DEVICE_ORDER_ERROR_STATUSES.includes(result.status)) {
               options.onDocumentVerificationErrors(result.validationErrors);
               return;
            }
            options.onSuccess();
            dispatchSnackbarPositiveEvent(
               createSingleInterpolatedTranslationKey(
                  medicalDevicesTranslationKeys.ezwmSuccesfullySent,
                  'pin',
                  result.pin
               )
            );
            changeTab(visitPaths.MedicalDevicesPanelNavParams.ORDERS_HISTORY);
         },
         onFault: options.onEzwmLoginError
            ? {
                 ...certificateFaultHandlers,
                 'egabinet-ezwm-frontend-api:EzwmAccountLoginFault': options.onEzwmLoginError,
                 'egabinet-ezwm-frontend-api:EzwmAccountBlockedFault': options.onEzwmLoginError,
                 'egabinet-ezwm-frontend-api:EzwmPasswordExpiredFault': options.onEzwmLoginError,
              }
            : undefined,
      }
   );
};

type UseMedicalDeviceOrderSubmitOptions = UseMedicalDeviceOrderApproveCommandOptions;

type UseMedicalDeviceOrderSubmitReturn = {
   submit: (formData: MedicalDeviceOrderAllFieldsFormBean) => Promise<void>;
   renderMfaModal: () => ReactElement | null;
};

export const useMedicalDeviceOrderSubmit = (
   options: UseMedicalDeviceOrderSubmitOptions
): UseMedicalDeviceOrderSubmitReturn => {
   const { onEzwmLoginError } = options;
   const { executeVisitDocumentCommand } = useVisitDocumentContext();
   const { checkMfaRequired, openMfaModal, renderMfaModal, mfaModalOpened } = useEzwmMfaGuard({
      blockingOperationLabel: medicalDevicesTranslationKeys.verifyingDocument,
   });

   const handleEzwmLoginError = useCallback(
      (fault: MedicalDeviceOrderEzwmAuthenticationFault): void => {
         if (mfaModalOpened && isNfzMfaLoginFault(fault)) {
            return;
         }

         onEzwmLoginError?.(fault);
      },
      [mfaModalOpened, onEzwmLoginError]
   );

   const { executeAsync: approveMedicalDeviceOrder } = useMedicalDeviceOrderApproveCommand({
      ...options,
      onEzwmLoginError: handleEzwmLoginError,
   });

   const executeSubmit = useCallback(
      async (formData: MedicalDeviceOrderAllFieldsFormBean, mfaTotp?: string): Promise<void> => {
         await executeVisitDocumentCommand(VisitMedicalDeviceOrderCreateDraftCommand, async (externalId) => {
            await approveMedicalDeviceOrder({ formData: formData, externalId: externalId, mfaTotp: mfaTotp });
         });
      },
      [approveMedicalDeviceOrder, executeVisitDocumentCommand]
   );

   const submit = useCallback(
      async (formData: MedicalDeviceOrderAllFieldsFormBean): Promise<void> => {
         const mfaRequired = await checkMfaRequired();

         if (mfaRequired) {
            openMfaModal(async (mfaTotp) => {
               await executeSubmit(formData, mfaTotp);
            });
            return;
         }

         await executeSubmit(formData);
      },
      [checkMfaRequired, openMfaModal, executeSubmit]
   );

   return {
      submit: submit,
      renderMfaModal: renderMfaModal,
   };
};
